import { useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import {
  Calendar,
  Users,
  DollarSign,
  TrendingUp,
  Clock,
  AlertTriangle,
  MessageSquare,
  ChevronRight,
  CalendarDays,
  MapPin,
  Loader2,
  CheckSquare,
  X,
  ExternalLink,
} from 'lucide-react';
import { EventStatus, SystemRole, formatCurrency } from '@peles/shared';
import { useAuth } from '../context/AuthContext';
import { useDashboardStats, useDashboardToday } from '../hooks/useDashboard';
import { useClientReceivables } from '../hooks/useClientPayments';
import { useToggleChecklistItem } from '../hooks/useEventOps';
import { useQueryClient } from '@tanstack/react-query';
import { TodayFocus } from '../components/dashboard/TodayFocus';
import { StatTile } from '../components/common/StatTile';
import { eventsListPath } from '../utils/eventListLink';

/** The dashboard's event counts leave out cancelled events, and only those. */
const NOT_CANCELLED = Object.values(EventStatus).filter((s) => s !== EventStatus.CANCELLED);
import type {
  UpcomingEvent,
  RecentInquiry,
  OverduePayment,
  UpcomingChecklistItem,
} from '../api/dashboard';
import { AssistantBriefingCard } from '../components/assistant/AssistantBriefingCard';

function formatDate(date: string): string {
  const d = new Date(date);
  const local = new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  return local.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function daysUntil(dateStr: string): number {
  const eventDate = new Date(dateStr);
  const now = new Date();
  const eventDay = new Date(eventDate.getFullYear(), eventDate.getMonth(), eventDate.getDate());
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((eventDay.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
}

// For date-only values stored as UTC midnight (e.g. task dueDates)
function calendarDaysUntil(dateStr: string): number {
  const d = new Date(dateStr);
  const eventDay = new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((eventDay.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
}

function timeAgo(date: string): string {
  const diff = Date.now() - new Date(date).getTime();
  const hours = Math.floor(diff / (1000 * 60 * 60));
  if (hours < 1) return 'just now';
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function formatDateRange(startAt: string, endAt: string | null): string {
  const start = new Date(startAt);
  const end = endAt ? new Date(endAt) : null;
  const sameDay = end ? start.toDateString() === end.toDateString() : false;
  if (!end) return formatDate(startAt);
  if (sameDay) return formatDate(startAt);
  // Compact range when same year/month: "Sep 12 – 13" or "Sep 12 – Oct 4"
  const sameMonth =
    start.getFullYear() === end.getFullYear() && start.getMonth() === end.getMonth();
  if (sameMonth) {
    return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${end.getDate()}`;
  }
  return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${end.toLocaleDateString(
    'en-US',
    { month: 'short', day: 'numeric' },
  )}`;
}

function daysLabel(days: number): string {
  if (days === 0) return 'Today';
  if (days === 1) return 'Tomorrow';
  if (days < 0) return `${Math.abs(days)}d ago`;
  return `in ${days} days`;
}

function UpcomingEventCard({ event, onClick }: { event: UpcomingEvent; onClick: () => void }) {
  const days = daysUntil(event.startAt);
  const daysColor =
    days <= 7
      ? 'bg-red-100 text-red-700'
      : days <= 30
        ? 'bg-amber-100 text-amber-700'
        : 'bg-primary-100 text-primary-700';
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-left bg-white rounded-xl shadow-sm border border-gray-100 p-4 hover:border-primary-300 hover:shadow-md transition-all flex flex-col gap-3"
    >
      <div className="flex items-start gap-3">
        {event.logoUrl ? (
          <img
            src={event.logoUrl}
            alt=""
            className="w-12 h-12 rounded-lg object-cover border border-gray-200 bg-white flex-shrink-0"
          />
        ) : (
          <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
            <CalendarDays size={20} className="text-gray-400" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900 truncate">{event.name}</p>
          <p className="text-xs text-gray-500 mt-0.5">
            {formatDateRange(event.startAt, event.endAt)}
          </p>
          <span
            className={`inline-block mt-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full ${daysColor}`}
          >
            {daysLabel(days)}
          </span>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 pt-2 border-t border-gray-100 text-center">
        <div>
          <p className="text-[10px] text-gray-400 uppercase tracking-wide">Guests</p>
          <p className="text-sm font-semibold text-gray-900 mt-0.5">
            {event.confirmedGuests}/{event.totalGuests}
          </p>
          {(event.pendingGuests > 0 || event.maybeGuests > 0 || event.declinedGuests > 0) && (
            <p className="text-[10px] text-gray-400 mt-0.5">
              {[
                event.pendingGuests > 0 && `${event.pendingGuests} pending`,
                event.maybeGuests > 0 && `${event.maybeGuests} maybe`,
                event.declinedGuests > 0 && `${event.declinedGuests} declined`,
              ]
                .filter(Boolean)
                .join(' · ')}
            </p>
          )}
        </div>
        <div>
          <p className="text-[10px] text-gray-400 uppercase tracking-wide">Expenses</p>
          <p className="text-sm font-semibold text-gray-900 mt-0.5">
            {formatCurrency(event.totalExpenses, { wholeDollars: true })}
          </p>
        </div>
        <div>
          <p className="text-[10px] text-gray-400 uppercase tracking-wide">Client owes</p>
          <p
            className={`text-sm font-semibold mt-0.5 ${
              event.balanceDue > 0.005 ? 'text-red-600' : 'text-green-700'
            }`}
          >
            {formatCurrency(event.balanceDue, { wholeDollars: true })}
          </p>
        </div>
      </div>
    </button>
  );
}

function UpcomingEventsSection({
  events,
  onEventClick,
}: {
  events: UpcomingEvent[];
  onEventClick: (id: string) => void;
}) {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
          <CalendarDays size={16} className="text-primary-600" />
          Upcoming Events
        </h3>
        <Link to="/events" className="text-xs text-primary-600 hover:text-primary-700">
          View all
        </Link>
      </div>
      {events.length === 0 ? (
        <p className="text-sm text-gray-400 py-4 text-center bg-white rounded-xl border border-gray-100">
          No upcoming events
        </p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {events.map((event) => (
            <UpcomingEventCard
              key={event.id}
              event={event}
              onClick={() => onEventClick(event.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function RecentInquiriesCard({ inquiries }: { inquiries: RecentInquiry[] }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
          <MessageSquare size={16} className="text-green-600" />
          Recent Inquiries
        </h3>
        <Link
          to="/communications/inquiries"
          className="text-xs text-primary-600 hover:text-primary-700"
        >
          View all
        </Link>
      </div>
      {inquiries.length === 0 ? (
        <p className="text-sm text-gray-400 py-4 text-center">No recent inquiries</p>
      ) : (
        <div className="space-y-3">
          {inquiries.map((inquiry) => (
            <div key={inquiry.id} className="flex items-center gap-3 p-2">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                <span className="text-xs font-bold text-green-700">
                  {inquiry.name.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{inquiry.name}</p>
                <p className="text-xs text-gray-500">
                  {inquiry.eventType || 'General inquiry'} · {inquiry.email}
                </p>
              </div>
              <span className="text-xs text-gray-400 flex-shrink-0">
                {timeAgo(inquiry.createdAt)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function OverduePaymentsCard({
  payments,
  onEventClick,
}: {
  payments: OverduePayment[];
  onEventClick: (eventId: string) => void;
}) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
          <AlertTriangle size={16} className="text-red-500" />
          Overdue Payments
        </h3>
      </div>
      {payments.length === 0 ? (
        <p className="text-sm text-gray-400 py-4 text-center">No overdue payments</p>
      ) : (
        <div className="space-y-2">
          {payments.map((p) => (
            <div
              key={p.id}
              onClick={() => onEventClick(p.eventId)}
              className="flex items-center justify-between p-2 rounded-lg hover:bg-red-50 cursor-pointer transition-colors"
            >
              <div className="min-w-0">
                <p className="text-sm text-gray-900 truncate">
                  {p.vendor || p.category || 'Unknown'}
                </p>
                <p className="text-xs text-gray-500">
                  {p.eventName}
                  {p.dueDate && ` · Due ${formatDate(p.dueDate)}`}
                </p>
              </div>
              <span className="text-sm font-semibold text-red-600 flex-shrink-0">
                {formatCurrency(p.balance ?? p.amount, { wholeDollars: true })}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TaskDetailModal({ item, onClose }: { item: UpcomingChecklistItem; onClose: () => void }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toggle = useToggleChecklistItem();

  function handleToggle() {
    toggle.mutate(
      { itemId: item.id, isDone: !item.isDone },
      {
        onSuccess: () => {
          qc.invalidateQueries({ queryKey: ['dashboard', 'stats'] });
          onClose();
        },
      },
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 space-y-4">
        <div className="flex items-start justify-between gap-2">
          <h2 className="text-base font-semibold text-gray-900 leading-snug">{item.title}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 shrink-0"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="space-y-2 text-sm text-gray-600">
          {item.eventName && (
            <div className="flex items-center gap-2">
              <CalendarDays size={14} className="text-primary-400 shrink-0" />
              <button
                onClick={() => {
                  onClose();
                  if (item.eventId) navigate(`/events/${item.eventId}`);
                }}
                className="hover:text-primary-600 font-medium flex items-center gap-1"
              >
                {item.eventName}
                <ExternalLink size={12} />
              </button>
            </div>
          )}
          {item.checklistTitle && (
            <div className="flex items-center gap-2">
              <CheckSquare size={14} className="text-purple-400 shrink-0" />
              <button
                onClick={() => {
                  onClose();
                  if (item.eventId) navigate(`/events/${item.eventId}/checklist`);
                  else navigate('/tasks');
                }}
                className="hover:text-primary-600 flex items-center gap-1"
              >
                {item.checklistTitle}
                <ExternalLink size={12} />
              </button>
            </div>
          )}
          {item.dueDate && (
            <div className="flex items-center gap-2">
              <Clock size={14} className="text-amber-400 shrink-0" />
              <span>
                Due:{' '}
                {(() => {
                  const d = new Date(item.dueDate!);
                  const local = new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
                  return local.toLocaleDateString('en-US', {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  });
                })()}
              </span>
            </div>
          )}
          {item.assigneeName && (
            <div className="flex items-center gap-2">
              <Users size={14} className="text-gray-400 shrink-0" />
              <span>{item.assigneeName}</span>
            </div>
          )}
          <div className="flex items-center gap-2">
            <span
              className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                item.status === 'BLOCKED'
                  ? 'bg-red-100 text-red-700'
                  : item.status === 'IN_PROGRESS'
                    ? 'bg-blue-100 text-blue-700'
                    : item.isDone
                      ? 'bg-green-100 text-green-700'
                      : 'bg-gray-100 text-gray-700'
              }`}
            >
              {item.isDone ? 'DONE' : item.status.replace('_', ' ')}
            </span>
          </div>
        </div>

        <div className="flex gap-2 pt-2">
          <button
            onClick={handleToggle}
            disabled={toggle.isPending}
            className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
              item.isDone
                ? 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                : 'bg-green-600 hover:bg-green-700 text-white'
            }`}
          >
            {toggle.isPending ? 'Saving…' : item.isDone ? 'Mark as Undone' : 'Mark as Done'}
          </button>
          {item.eventId && (
            <button
              onClick={() => {
                onClose();
                navigate(`/events/${item.eventId}/checklist`);
              }}
              className="px-3 py-2 rounded-lg text-sm border border-gray-300 hover:bg-gray-50 text-gray-700"
            >
              Open Task List
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function UpcomingChecklistCard({ items }: { items: UpcomingChecklistItem[] }) {
  const [selectedTask, setSelectedTask] = useState<UpcomingChecklistItem | null>(null);
  const qc = useQueryClient();
  const toggle = useToggleChecklistItem();

  function handleToggle(e: React.MouseEvent, item: UpcomingChecklistItem) {
    e.stopPropagation();
    toggle.mutate(
      { itemId: item.id, isDone: !item.isDone },
      { onSuccess: () => qc.invalidateQueries({ queryKey: ['dashboard', 'stats'] }) },
    );
  }

  return (
    <>
      {selectedTask && (
        <TaskDetailModal item={selectedTask} onClose={() => setSelectedTask(null)} />
      )}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
            <CheckSquare size={16} className="text-purple-600" />
            Upcoming Tasks
          </h3>
          <Link to="/tasks" className="text-xs text-primary-600 hover:text-primary-700">
            View all
          </Link>
        </div>
        {items.length === 0 ? (
          <p className="text-sm text-gray-400 py-4 text-center">No upcoming tasks</p>
        ) : (
          <div className="space-y-1">
            {items.map((item) => {
              const days = item.dueDate ? calendarDaysUntil(item.dueDate) : null;
              const isOverdue = days !== null && days < 0;
              return (
                <div
                  key={item.id}
                  onClick={() => setSelectedTask(item)}
                  className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors ${isOverdue ? 'hover:bg-red-50' : 'hover:bg-gray-50'}`}
                >
                  <button
                    onClick={(e) => handleToggle(e, item)}
                    disabled={toggle.isPending}
                    className="shrink-0 text-gray-300 hover:text-green-500 transition-colors"
                    title={item.isDone ? 'Mark undone' : 'Mark done'}
                  >
                    <CheckSquare
                      size={16}
                      className={item.isDone ? 'text-green-500' : 'text-gray-300'}
                    />
                  </button>
                  <div className="min-w-0 flex-1">
                    <p
                      className={`text-sm truncate ${
                        item.isDone
                          ? 'line-through text-gray-400'
                          : isOverdue
                            ? 'text-red-600 font-medium'
                            : 'text-gray-900'
                      }`}
                    >
                      {item.title}
                    </p>
                    <p className="text-xs text-gray-500 truncate">
                      {item.eventName || 'No event'}
                      {item.assigneeName && ` · ${item.assigneeName}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    {days !== null && (
                      <span
                        className={`text-xs font-medium ${
                          isOverdue
                            ? 'text-red-600'
                            : days <= 2
                              ? 'text-red-600'
                              : days <= 5
                                ? 'text-amber-600'
                                : 'text-gray-400'
                        }`}
                      >
                        {isOverdue
                          ? `${Math.abs(days)}d ago`
                          : days === 0
                            ? 'Today'
                            : days === 1
                              ? 'Tomorrow'
                              : `${days}d`}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}

function PlannerDashboard() {
  const navigate = useNavigate();
  const { data, isLoading } = useDashboardStats();
  const today = useDashboardToday();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={32} className="animate-spin text-primary-600" />
      </div>
    );
  }

  const stats = data?.stats;

  return (
    <div>
      {/* Today first: what is on, and what needs doing. */}
      <TodayFocus
        today={today.data}
        isLoading={today.isLoading}
        isError={today.isError}
        onRetry={() => void today.refetch()}
      />

      {/* The assistant's reading of the day, with what to do first. */}
      <AssistantBriefingCard className="mb-6 block" />

      {/* Upcoming Events + Tasks — two columns */}
      <div className="mb-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <UpcomingEventsSection
          events={data?.upcomingEvents ?? []}
          onEventClick={(id) => navigate(`/events/${id}`)}
        />
        <UpcomingChecklistCard items={data?.upcomingChecklistItems ?? []} />
      </div>

      {/* The totals: useful, but not what the day starts with. */}
      <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">
        At a glance
      </h2>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
        <StatTile
          size="large"
          label="Total Events"
          value={stats?.totalEvents ?? 0}
          icon={Calendar}
          color="bg-primary-600"
          // Counted as every event but the cancelled — the All range's default.
          to={eventsListPath({ range: 'all' })}
        />
        <StatTile
          size="large"
          label="Upcoming (30d)"
          value={stats?.upcomingEvents ?? 0}
          icon={Clock}
          color="bg-amber-500"
          to={eventsListPath({ range: 'month', statuses: NOT_CANCELLED })}
        />
        <StatTile
          size="large"
          label="Revenue (YTD)"
          value={formatCurrency(stats?.revenueThisYear ?? 0, { wholeDollars: true })}
          icon={TrendingUp}
          color="bg-green-500"
        />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatTile
          size="large"
          label="Unpaid Expenses"
          value={formatCurrency(stats?.unpaidTotal ?? 0, { wholeDollars: true })}
          icon={DollarSign}
          color="bg-red-500"
        />
        <StatTile
          size="large"
          label="New Inquiries (7d)"
          value={stats?.recentInquiries ?? 0}
          icon={MessageSquare}
          color="bg-green-500"
          subtext="Last 7 days"
          to="/communications/inquiries"
        />
        <StatTile
          size="large"
          label="Total Profit"
          value={formatCurrency(stats?.totalProfit ?? 0, { wholeDollars: true })}
          icon={TrendingUp}
          color={(stats?.totalProfit ?? 0) >= 0 ? 'bg-emerald-600' : 'bg-amber-600'}
          subtext="Commissions + Producer expenses"
        />
      </div>

      {/* Recent Inquiries */}
      <div className="mb-6">
        <RecentInquiriesCard inquiries={data?.recentInquiries ?? []} />
      </div>

      {/* Overdue Payments */}
      {(data?.overduePayments?.length ?? 0) > 0 && (
        <div className="mt-6">
          <OverduePaymentsCard
            payments={data?.overduePayments ?? []}
            onEventClick={(eventId) => navigate(`/events/${eventId}/expenses`)}
          />
        </div>
      )}

      {/* Outstanding Client Balances */}
      <ClientReceivablesWidget onEventClick={(id) => navigate(`/events/${id}`)} />

      {/* Quick Actions */}
      <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'New Event', href: '/events?new=1', icon: Calendar },
          { label: 'View Guests', href: '/guests', icon: Users },
          { label: 'Expenses', href: '/expenses', icon: DollarSign },
          { label: 'Floor Plans', href: '/floor-plans', icon: MapPin },
        ].map(({ label, href, icon: Icon }) => (
          <Link
            key={label}
            to={href}
            className="flex items-center gap-2 p-3 bg-white rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-colors text-sm text-gray-700"
          >
            <Icon size={16} className="text-primary-600" />
            {label}
            <ChevronRight size={14} className="ml-auto text-gray-400" />
          </Link>
        ))}
      </div>
    </div>
  );
}

export function HomePage() {
  const { user } = useAuth();

  if (user?.systemRole === SystemRole.CLIENT) {
    return <Navigate to="/client" replace />;
  }

  if (user?.systemRole === SystemRole.VENDOR) {
    return <Navigate to="/vendor" replace />;
  }

  // Admin/Planner gets the full dashboard
  return <PlannerDashboard />;
}

function ClientReceivablesWidget({ onEventClick }: { onEventClick: (id: string) => void }) {
  const { data, isLoading } = useClientReceivables();
  if (isLoading || !data || data.totalBalance <= 0.005) return null;
  const top5 = data.rows.filter((r) => r.balance > 0.005).slice(0, 5);
  return (
    <div
      className="mt-6 bg-white rounded-xl border border-gray-200 p-4"
      data-testid="client-receivables-widget"
    >
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-base font-semibold text-gray-900">Outstanding Client Balances</h3>
        <span className="text-sm font-bold text-red-600">
          {formatCurrency(data.totalBalance, { wholeDollars: true })} owed
        </span>
      </div>
      {top5.length === 0 ? (
        <p className="text-sm text-gray-500 italic">No outstanding balances.</p>
      ) : (
        <ul className="divide-y divide-gray-100">
          {top5.map((row) => (
            <li
              key={row.eventId}
              className="flex items-center justify-between py-2 cursor-pointer hover:bg-gray-50 -mx-2 px-2 rounded"
              onClick={() => onEventClick(row.eventId)}
            >
              <div className="text-sm">
                <div className="font-medium text-gray-900">{row.eventName}</div>
                <div className="text-xs text-gray-500">
                  Owed {formatCurrency(row.totalOwed, { wholeDollars: true })} • Received{' '}
                  {formatCurrency(row.totalReceived, { wholeDollars: true })}
                </div>
              </div>
              <div className="text-sm font-semibold text-red-600">
                {formatCurrency(row.balance, { wholeDollars: true })}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
